import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Loader2, GitBranch, AlertTriangle, CheckCircle2, ShieldAlert } from 'lucide-react';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

/**
 * br_ltd Xero Receivable Provenance (Phase A follow-up, read-only).
 *
 * Splits the al-invoice-drilldown receivable population (Xero-sourced
 * subset) into TRACE / SERIES / NEITHER / NON_XERO_SOURCE, reconciles
 * bucket counts+amounts against that same population to the cent, and
 * reads BR OS ingestion state for the five PHD Stillads sample numbers.
 * No dedup decision happens here -- this only produces the split.
 */
function fmt(n) {
  if (n === null || n === undefined) return '-';
  return Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

var BUCKET_META = {
  TRACE: { label: 'TRACE', hint: 'recorded Xero ID link -- deterministic', variant: 'default' },
  SERIES: { label: 'SERIES', hint: 'numbering-series overlap only', variant: 'secondary' },
  NEITHER: { label: 'NEITHER', hint: 'no signal either way', variant: 'outline' },
  NON_XERO_SOURCE: { label: 'NON_XERO_SOURCE', hint: 'outside the Xero-push question', variant: 'outline' },
};

function totalsLine(totalsByCurrency) {
  var entries = Object.entries(totalsByCurrency || {});
  if (entries.length === 0) return '0.00';
  return entries.map(function (e) {
    return fmt(e[1].amount_native) + ' ' + e[0];
  }).join(', ');
}

export function AlInvoiceProvenancePanel() {
  var [loading, setLoading] = useState(false);
  var [result, setResult] = useState(null);
  var [dedupPreview, setDedupPreview] = useState(null);
  var [dedupPreviewLoading, setDedupPreviewLoading] = useState(false);
  var [dedupApplyLoading, setDedupApplyLoading] = useState(false);

  var run = async function () {
    setLoading(true);
    setResult(null);
    try {
      var res = await diagnosticsAPI.alInvoiceProvenance('br_ltd');
      setResult(res.data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  var runDedupPreview = async function () {
    setDedupPreviewLoading(true);
    try {
      var res = await diagnosticsAPI.previewXeroTraceDedup();
      setDedupPreview(res.data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setDedupPreviewLoading(false);
    }
  };

  var runDedupApply = async function () {
    if (!window.confirm(
      (dedupPreview?.eliminated_addition?.row_count || 0) + ' TRACE-linked Xero invoice(s) will leave ' +
      'the br_ltd receivable and join eliminated overlap at their authoritative bros_invoices amount. ' +
      'This does not mutate any invoice document -- it only arms a flag the A&L balance sheet and the ' +
      'drilldown card read at query time. First activation, one-way.'
    )) return;
    setDedupApplyLoading(true);
    try {
      await diagnosticsAPI.applyXeroTraceDedup();
      toast.success('Xero TRACE dedup armed.');
      await run();
      setDedupPreview(null);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setDedupApplyLoading(false);
    }
  };

  var recon = result?.reconciliation;
  var reconOk = recon && recon.count_reconciles && recon.amounts_reconcile;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <GitBranch className="h-4 w-4" />
          br_ltd Xero Receivable Provenance
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-muted-foreground">
          Splits the al-invoice-drilldown receivable population into TRACE (recorded
          Xero ID link), SERIES (numbering-series overlap only, no trace), NEITHER, and
          NON_XERO_SOURCE (kept for reconciliation completeness). Read-only, br_ltd only.
        </p>
        <Button size="sm" onClick={run} disabled={loading}>
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Run for br_ltd'}
        </Button>

        {result && !result.applicable && (
          <div className="bg-muted/50 rounded p-3 text-xs text-muted-foreground">{result.note}</div>
        )}

        {result && result.applicable && (
          <div className="space-y-4 text-sm">
            <div className="flex items-center gap-2">
              <span className="font-semibold">{result.entity_name}</span>
              <Badge variant="outline" className="font-mono text-xs">{result.entity_id}</Badge>
              <span className="text-xs text-muted-foreground">population: {result.population_count}</span>
              {result.live_exclusion_active != null && (
                <Badge variant={result.live_exclusion_active ? 'default' : 'outline'} data-testid="xero-trace-dedup-status">
                  {result.live_exclusion_active ? 'TRACE dedup active' : 'TRACE dedup not activated'}
                </Badge>
              )}
            </div>

            {/* TRACE dedup Preview/Apply -- first-activation gate for the
                exclusion the buckets above are evidence for. */}
            {!result.live_exclusion_active && (
              <div className="border rounded p-3 space-y-2">
                <div className="flex items-center gap-2">
                  <ShieldAlert className="h-4 w-4 text-muted-foreground" />
                  <span className="font-semibold text-xs">TRACE dedup activation</span>
                </div>
                <p className="text-xs text-muted-foreground">
                  Excludes TRACE-linked Xero invoices from the br_ltd receivable and routes them into
                  eliminated overlap at their authoritative bros_invoices amount. Preview first, then Apply.
                </p>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={runDedupPreview} disabled={dedupPreviewLoading} data-testid="xero-trace-dedup-preview">
                    {dedupPreviewLoading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
                    Preview
                  </Button>
                  {dedupPreview && dedupPreview.eliminated_addition?.row_count > 0 && (
                    <Button
                      size="sm"
                      onClick={runDedupApply}
                      disabled={dedupApplyLoading}
                      data-testid="xero-trace-dedup-apply"
                    >
                      {dedupApplyLoading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
                      Apply to {dedupPreview.eliminated_addition.row_count} Invoices
                    </Button>
                  )}
                </div>
                {dedupPreview && (
                  <div className="space-y-2 text-xs" data-testid="xero-trace-dedup-preview-result">
                    <p className="text-muted-foreground">{dedupPreview.message}</p>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <div className="text-muted-foreground">Before</div>
                        <div className="font-mono">{totalsLine(dedupPreview.before.totals_by_currency)}</div>
                      </div>
                      <div>
                        <div className="text-muted-foreground">After</div>
                        <div className="font-mono">{totalsLine(dedupPreview.after.totals_by_currency)}</div>
                      </div>
                    </div>
                    {dedupPreview.moving_rows?.length > 0 && (
                      <div className="overflow-x-auto max-h-48 overflow-y-auto">
                        <table className="w-full border-collapse">
                          <thead>
                            <tr className="border-b text-left">
                              <th className="py-0.5 pr-1">number</th>
                              <th className="py-0.5 pr-1 text-right">xero amount</th>
                              <th className="py-0.5 pr-1 text-right">authoritative</th>
                              <th className="py-0.5 pr-1">matched bros</th>
                            </tr>
                          </thead>
                          <tbody>
                            {dedupPreview.moving_rows.map(function (r, i) {
                              return (
                                <tr key={i} className="border-b border-border/20">
                                  <td className="py-0.5 pr-1 font-mono">{r.invoice_number || r.id}</td>
                                  <td className="py-0.5 pr-1 font-mono text-right">{fmt(r.xero_amount)} {r.currency}</td>
                                  <td className="py-0.5 pr-1 font-mono text-right">{fmt(r.authoritative_amount)} {r.matched_bros_invoice?.currency}</td>
                                  <td className="py-0.5 pr-1 font-mono">{r.matched_bros_invoice?.invoice_number || r.matched_bros_invoice?.bros_id}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                        {dedupPreview.moving_rows_truncated && (
                          <p className="text-muted-foreground mt-1">Sample truncated.</p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Reconciliation line */}
            <div className={
              'rounded p-3 border flex items-center gap-2 ' +
              (reconOk
                ? 'bg-muted/50 border-transparent'
                : 'bg-red-50 dark:bg-red-950/30 border-red-200 dark:border-red-900')
            }>
              {reconOk
                ? <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" />
                : <AlertTriangle className="h-4 w-4 text-red-600 shrink-0" />}
              <div className="text-xs">
                <div className="font-semibold">
                  {reconOk ? 'Buckets reconcile to the population' : 'Reconciliation discrepancy'}
                </div>
                <div>
                  counts: {recon.bucket_count_sum} / {recon.population_count}
                  {' '}({recon.count_reconciles ? 'match' : 'MISMATCH'})
                </div>
                {Object.entries(recon.by_currency || {}).map(function (e) {
                  var cur = e[0], row = e[1];
                  return (
                    <div key={cur} className={row.reconciles ? '' : 'text-red-600 font-semibold'}>
                      {cur}: population {fmt(row.population_amount_native)} vs buckets {fmt(row.bucket_amount_sum_native)}
                      {' '}({row.reconciles ? 'match' : 'MISMATCH'})
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Bucket cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {Object.entries(result.buckets || {}).map(function (e) {
                var key = e[0], bucket = e[1];
                var meta = BUCKET_META[key] || { label: key, hint: '', variant: 'outline' };
                return (
                  <div key={key} className="border rounded p-3">
                    <div className="flex items-center justify-between mb-1">
                      <Badge variant={meta.variant} className="text-[10px]">{meta.label}</Badge>
                      <span className="font-mono text-xs">{bucket.count}</span>
                    </div>
                    <div className="text-[11px] text-muted-foreground mb-1">{meta.hint}</div>
                    <div className="font-mono text-xs">{totalsLine(bucket.totals_by_currency)}</div>
                    {bucket.sample && bucket.sample.length > 0 && (
                      <div className="mt-2 overflow-x-auto max-h-40 overflow-y-auto">
                        <table className="text-[11px] w-full border-collapse">
                          <thead>
                            <tr className="border-b text-left">
                              <th className="py-0.5 pr-1">number</th>
                              <th className="py-0.5 pr-1 text-right">amount</th>
                              <th className="py-0.5 pr-1">matched bros</th>
                            </tr>
                          </thead>
                          <tbody>
                            {bucket.sample.map(function (r, i) {
                              return (
                                <tr key={i} className="border-b border-border/20">
                                  <td className="py-0.5 pr-1 font-mono">{r.invoice_number || '-'}</td>
                                  <td className="py-0.5 pr-1 font-mono text-right">{fmt(r.amount)} {r.currency}</td>
                                  <td className="py-0.5 pr-1 font-mono">
                                    {r.matched_bros_invoice
                                      ? (r.matched_bros_invoice.invoice_number || r.matched_bros_invoice.bros_id) + ' [' + r.matched_bros_invoice.ingestion_status + ']'
                                      : '-'}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* PHD Stillads sample */}
            {result.phd_stillads_sample && (
              <div>
                <h5 className="font-semibold text-xs mb-1">PHD Stillads sample (bros-side pairing)</h5>
                <div className="overflow-x-auto">
                  <table className="text-xs w-full border-collapse">
                    <thead>
                      <tr className="border-b text-left">
                        <th className="py-1 pr-2">number</th>
                        <th className="py-1 pr-2">bucket</th>
                        <th className="py-1 pr-2">stays in receivable?</th>
                        <th className="py-1 pr-2">matched bros</th>
                        <th className="py-1 pr-2">ingestion_status</th>
                        <th className="py-1 pr-2">twin-excluded?</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.phd_stillads_sample.rows.map(function (r, i) {
                        return (
                          <tr key={i} className={'border-b border-border/30' + (r.is_twin_excluded_state ? ' bg-amber-50 dark:bg-amber-950/30' : '')}>
                            <td className="py-1 pr-2 font-mono">{r.invoice_number}</td>
                            <td className="py-1 pr-2">
                              {r.provenance_bucket
                                ? <Badge variant={(BUCKET_META[r.provenance_bucket] || {}).variant || 'outline'} className="text-[10px]">{r.provenance_bucket}</Badge>
                                : <span className="text-muted-foreground text-[10px]">no receivable row</span>}
                            </td>
                            <td className="py-1 pr-2">
                              {r.stays_in_receivable
                                ? <Badge variant="outline" className="text-[10px]">yes</Badge>
                                : <Badge variant="destructive" className="text-[10px]">no, excluded</Badge>}
                            </td>
                            <td className="py-1 pr-2 font-mono">{r.bros_invoice_found ? (r.bros_id || '-') : '-'}</td>
                            <td className="py-1 pr-2 font-mono">{r.ingestion_status || '-'}</td>
                            <td className="py-1 pr-2">
                              {r.is_twin_excluded_state
                                ? <Badge variant="destructive" className="text-[10px]">yes</Badge>
                                : <span className="text-muted-foreground text-[10px]">-</span>}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  <table className="text-[11px] w-full border-collapse mt-1">
                    <tbody>
                      {result.phd_stillads_sample.rows.map(function (r, i) {
                        return (
                          <tr key={i}>
                            <td className="py-0.5 pr-2 font-mono text-muted-foreground">{r.invoice_number}</td>
                            <td className="py-0.5 text-muted-foreground">{r.explanation}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <p className="text-xs text-muted-foreground mt-1">{result.phd_stillads_sample.note}</p>
              </div>
            )}

            {result.note && (
              <div className="bg-muted/50 rounded p-2 text-xs text-muted-foreground">
                {result.note}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
